import { useEffect, useRef, useState } from 'react';

import { checkZipCodeEligibilityUrl } from 'src/app/client/api';
import Spinner from 'src/app/components/Elements/Spinner';
import { useFacilityByAccountId, useFacilityByZipCode } from 'src/app/hooks/data/facility';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import * as actionTypes from 'src/app/state/actions/types';
import { SET_CURRENT_ONBOARDING_STEP } from 'src/app/state/actions/types';
import { getAccountZipCode, getOnboardingCurrentStep } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import { extractErrorCodes } from 'src/app/utils';
import { isHydra } from 'src/app/utils/browser';
import { CAPSULE_ICON_STRINGS } from 'src/consts';
import { getAnonUserId } from 'src/landing-pages/utils';

import CapsuleIcon from '../../Elements/CapsuleIcon';

import { FLOW } from './consts';
import type { PropsFromRedux } from './container';
import { STEP_NAME } from './StepLayout/consts';
import { StepLayout } from './StepLayout/StepLayout';
import {
  FooterRow,
  GoogleRecaptchaDisclaimerContainer,
  GoogleRecaptchaIcon,
  GoogleRecaptchaText,
  MainRow,
  MainSection,
  OnboardingContainer,
} from './styles';
import { useSetInitialOnboardingStepAndParams } from './useSetInitialOnboardingStep';
import { clearOnboardingStorage } from './util';

export const GetStarted = ({
  onboardingSteps,
  onboardingForm,
  currentStep,
  currentStepIndex,
  zipCodeParam,
  isDeliverableParam,
  onSetCurrentOnboardingStep,
  onSetInitialOnboardingStep,
  onSetOnboardingFormValues,
  onHydrateStoredFields,
  onValidateEmail,
  stepFromQueryString,
  setShowHorizontalProgressBar,
  checkZipCodeEligibility,
  isZipCodeDeliverable,
  dispatch,
  onSetOnboardingFlow,
  onAddStepAfterKey,
  onboardingZipCode,
}: PropsFromRedux) => {
  useSegmentEvent('viewAddFamilyMemberConfirmation', { shouldTrackOnMount: true });

  const isDeliverableRef = useRef(isZipCodeDeliverable);
  const [showFooter, setShowFooter] = useState(true);
  const [isOnSubpage, setIsOnSubpage] = useState(false);
  const { loggedInUser } = useAuth();
  const currentOnboardingStep = useSelector(getOnboardingCurrentStep);
  const accountZipCode = useSelector(getAccountZipCode);
  const dispatchErrorSegmentEvent = useSegmentEvent('error', { shouldTrackOnMount: false });

  const isEmailOrCaptchaStep = stepFromQueryString === STEP_NAME.CONTACT || stepFromQueryString === STEP_NAME.CAPTCHA;

  useEffect(() => {
    setShowHorizontalProgressBar(true);
  }, [setShowHorizontalProgressBar]);

  const [
    shouldShowTransferFormFullProfileTransferInExperience,
    setShouldShowTransferFormFullProfileTransferInExperience,
  ] = useState(false);
  const { fetchVariant } = useExperiments();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const { data: facility } = useFacilityByAccountId(user ? parseInt(user.accountId) : 0);
  const { data: facility_from_onboarding_zipcode } = useFacilityByZipCode(onboardingZipCode);

  useEffect(() => {
    if (!!user) {
      dispatch({
        type: actionTypes.SEGMENT_IDENTIFY,
        accountId: user.accountId,
      });
    }
  }, [dispatch, user]);

  useEffect(() => {
    const setNewTransferFormExperiences = async () => {
      const fullProfileTransferInExperimentResult = await fetchVariant({
        experimentKey: 'mcyp_transfer_form_full_profile_transfer_in',
        userId: user?.accountId || getAnonUserId(),
        audienceAttributes: {
          // This experiment uses facility zip because code is not available for logged-out users
          facility_zip: facility_from_onboarding_zipcode?.address?.zipCode || facility?.address?.zipCode,
        },
      });
      const shouldShowFullProfileTransferIn = fullProfileTransferInExperimentResult === 'show_experience';
      setShouldShowTransferFormFullProfileTransferInExperience(shouldShowFullProfileTransferIn);
    };
    if (!isHydra()) {
      setNewTransferFormExperiences();
    }
  }, [user, fetchVariant, facility, facility_from_onboarding_zipcode]);

  const isLoadingEntry = useSetInitialOnboardingStepAndParams({
    onHydrateStoredFields,
    onValidateEmail,
    clearOnboardingStorage,
    onSetInitialOnboardingStep,
    zipCodeParam,
    isDeliverableParam,
    onSetOnboardingFormValues,
    onSetCurrentOnboardingStep,
    onAddStepAfterKey,
    currentOnboardingStep,
  });

  useEffect(() => {
    if (STEP_NAME[stepFromQueryString]) {
      if (currentStep !== stepFromQueryString) {
        onSetCurrentOnboardingStep(stepFromQueryString);
      }
    }
  }, [currentStep, onSetCurrentOnboardingStep, stepFromQueryString]);

  useEffect(() => {
    // Note: Scroll to top only on step change
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    onSetOnboardingFlow(FLOW.TRANSFER);
  }, [onSetOnboardingFlow]);

  useEffect(() => {
    const getIndexOfStep = stepName => {
      return onboardingSteps?.findIndex(
        step => stepName === step.stepName || step.additionalLayouts.includes(stepName)
      );
    };
    // Prevent users from visiting steps without inputting data required by previous steps.
    const welcomeIdx = getIndexOfStep(STEP_NAME.WELCOME);
    const contactIdx = getIndexOfStep(STEP_NAME.CONTACT);
    const zipCode = onboardingForm.zipCode;
    const email = onboardingForm.email;

    if (currentStepIndex > welcomeIdx) {
      if (!zipCode) {
        onSetInitialOnboardingStep(STEP_NAME.WELCOME);
      }
    } else if (currentStepIndex > contactIdx && !email) {
      onSetInitialOnboardingStep(STEP_NAME.CONTACT);
    }
  }, [onboardingSteps, currentStep, stepFromQueryString, currentStepIndex, onSetInitialOnboardingStep, onboardingForm]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const getGeoCoordinates = async () => {
      if (!(onboardingForm.latitude && onboardingForm.longitude)) {
        if (onboardingForm.zipCode) {
          try {
            const resp = await checkZipCodeEligibility(onboardingForm.zipCode);

            onSetOnboardingFormValues({
              latitude: resp.data.latitude,
              longitude: resp.data.longitude,
            });
          } catch (error) {
            const bffError = error?.error ?? '';
            const api = checkZipCodeEligibilityUrl(onboardingForm.zipCode);
            const trackErrorPayload = {
              errorMessage: 'Check Zip Code Eligibility Failed',
              errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
              api,
              errorCode: extractErrorCodes(error),
              source: window.location.href,
            };
            dispatchErrorSegmentEvent(trackErrorPayload);
          }
        }
      }
    };
    getGeoCoordinates();
  }, [onboardingForm.zipCode, accountZipCode]);

  // update isDeliverable so unmount effect has latest state
  // Need a separate hook to avoid cleanup func getting called too many times
  useEffect(() => {
    isDeliverableRef.current = isZipCodeDeliverable;
  }, [isZipCodeDeliverable]);

  // On unmount clear zip state when zip is not deliverable
  useEffect(() => {
    return () => {
      if (!isDeliverableRef.current) {
        onSetOnboardingFormValues({
          zipCode: '',
          isZipCodeDeliverable: null,
        });
        // Updating step state without changing url to avoid resetting user navigation
        dispatch({ type: SET_CURRENT_ONBOARDING_STEP, currentStep: STEP_NAME.WELCOME });
      }
    };
    // This cleanup function should be run strictly on unmount which is tricky with dependencies
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  if (isLoadingEntry) return <Spinner centered />;

  return (
    <OnboardingContainer>
      <MainRow isSubpage={isOnSubpage} centerContent>
        <MainSection>
          <StepLayout
            currentStep={currentStep}
            setShowHorizontalProgressBar={setShowHorizontalProgressBar}
            setShowFooter={setShowFooter}
            setIsOnSubpage={setIsOnSubpage}
            shouldShowTransferFormFullProfileTransferInExperience={
              shouldShowTransferFormFullProfileTransferInExperience
            }
          />
        </MainSection>
      </MainRow>
      <FooterRow>
        {showFooter && isEmailOrCaptchaStep && (
          <GoogleRecaptchaDisclaimerContainer>
            <GoogleRecaptchaIcon>
              <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.lock_secure} />
            </GoogleRecaptchaIcon>
            <GoogleRecaptchaText>
              Protected by reCAPTCHA. Google's <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
              <a href="https://policies.google.com/terms">Terms of Service</a> apply.
            </GoogleRecaptchaText>
          </GoogleRecaptchaDisclaimerContainer>
        )}
      </FooterRow>
    </OnboardingContainer>
  );
};
